// Persistent tombstone set of message content-hashes the local user removed
// "for me" (delete-for-me). Consulted by `webMessagesPersister.put` so a WS
// resume / history replay can NOT resurrect a message the user deleted only on
// this device.
//
// Delete scoping (see WebChatRoom `handleMenuAction` 'remove'):
//   - deleting a PEER's message → local-only removal → tombstone the hash here
//     (no `com.ping.redact` broadcast, so peers/author keep the message);
//   - deleting your OWN message → `com.ping.redact` for everyone, and we also
//     tombstone locally so the redact's own echo can't re-add it.
//
// Backed by localStorage (small, bounded, synchronous) — no Dexie schema bump.

const KEY = 'ping.locallyRemovedMessages.v1';
const MAX = 5_000; // bound growth; keep the most-recent entries

let cache: Set<string> | null = null;

function load(): Set<string> {
  try {
    if (typeof localStorage === 'undefined') return new Set();
    const raw = localStorage.getItem(KEY);
    if (raw === null) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr)
      ? new Set(arr.filter((x): x is string => typeof x === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

function get(): Set<string> {
  if (cache === null) cache = load();
  return cache;
}

function save(set: Set<string>): void {
  try {
    if (typeof localStorage === 'undefined') return;
    // Set preserves insertion order — keep the newest MAX entries.
    const arr = [...set];
    const bounded = arr.length > MAX ? arr.slice(arr.length - MAX) : arr;
    localStorage.setItem(KEY, JSON.stringify(bounded));
  } catch {
    // storage full / unavailable — best-effort, tombstone is durability-nice-to-have.
  }
}

/** Record a content-hash as removed-for-me on this device. */
export async function addLocallyRemovedMessage(
  _conversationId: string,
  contentHash: string,
): Promise<void> {
  if (contentHash.length === 0) return;
  const set = get();
  if (set.has(contentHash)) return;
  set.add(contentHash);
  save(set);
}

/** True when this content-hash was removed-for-me and must not be re-inserted. */
export function isLocallyRemovedMessage(contentHash: string | undefined): boolean {
  if (contentHash === undefined || contentHash.length === 0) return false;
  return get().has(contentHash);
}
